/**
 * Activity Result Step
 *
 * Shows calculation results after creating or updating an activity.
 * Displays CO2e, notices (errors/warnings/info), and calculation details.
 */

import { useMemo } from 'react';
import { CheckCircle, AlertTriangle, XCircle, List, ExternalLink } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import {
  CalculationStatus,
  CalculationNoticeType,
  type ActivityWithCalculation,
  type EmissionEvidence,
} from '../types/emissions';
import { EVIDENCE_TYPE_LABEL, getMimeIcon } from '../utils/evidenceDisplay';

interface ActivityResultStepProps {
  result: ActivityWithCalculation;
  isEditing: boolean;
  /**
   * Evidences linked to this activity in the preceding Attach step.
   * Required (pass `[]` for flows with no attach step) so we don't need a
   * default-value fallback — defaults create a fresh `[]` reference every
   * render and would defeat the `uniqueEvidences` useMemo below.
   */
  attachedEvidences: EmissionEvidence[];
  formatCompact: (value: number) => { formatted: string; unitCompact: string };
  onViewActivities: () => void;
  onAddAnother: () => void;
  /**
   * Optional jump to the new full-page Activity Detail view for the
   * just-created activity. Wizard wires this to the EMISSIONS_ACTIVITY_DETAIL
   * route. Omit for flows where the detail page isn't the right destination
   * (e.g. wizard variants that haven't migrated yet).
   */
  onViewActivity?: () => void;
}

export const ActivityResultStep = ({ result, isEditing, attachedEvidences, formatCompact, onViewActivities, onAddAnother, onViewActivity }: ActivityResultStepProps) => {
  const { calculation, notices } = result;
  const status = calculation?.status;
  const isSuccess = status === CalculationStatus.Calculated;
  const isFailed = status === CalculationStatus.Failed;
  const warnings = notices?.filter((n) => n.type === CalculationNoticeType.Warning) ?? [];
  const errors = notices?.filter((n) => n.type === CalculationNoticeType.Error) ?? [];
  const infos = notices?.filter((n) => n.type === CalculationNoticeType.Info) ?? [];
  const co2e = calculation?.co2e;
  const co2eFormatted = co2e !== undefined && co2e !== null ? formatCompact(co2e) : null;
  // Dedup by _id: two uploads with identical bytes map to the same evidence
  // record (server SHA-256 dedup), so we'd otherwise render the same row twice.
  const uniqueEvidences = useMemo(
    () => Array.from(new Map(attachedEvidences.map((ev) => [ev._id, ev])).values()),
    [attachedEvidences],
  );

  return (
    <Card>
      <CardContent className='tw:p-8 tw:text-center'>
        <div
          className={cn(
            'tw:rounded-full tw:inline-flex tw:items-center tw:justify-center tw:mb-4',
            isSuccess ? 'tw:bg-emerald-100' : isFailed ? 'tw:bg-red-100' : 'tw:bg-amber-100'
          )}
          style={{ width: 64, height: 64 }}
        >
          {isSuccess ? (
            <CheckCircle className='tw:size-8 tw:text-emerald-600' />
          ) : isFailed ? (
            <XCircle className='tw:size-8 tw:text-red-600' />
          ) : (
            <AlertTriangle className='tw:size-8 tw:text-amber-600' />
          )}
        </div>

        <h5 className='tw:font-semibold tw:mb-2'>
          Activity {isEditing ? 'Updated' : 'Created'}
        </h5>

        {/* CO2e result */}
        {isSuccess && co2eFormatted && (
          <div className='tw:mb-4'>
            <span className='tw:text-3xl tw:font-bold'>{co2eFormatted.formatted}</span>
            <span className='tw:text-muted-foreground tw:ml-2'>{co2eFormatted.unitCompact}</span>
          </div>
        )}

        {!isSuccess && (
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-4'>
            The activity was saved but the emission calculation {isFailed ? 'failed' : 'is pending'}.
          </p>
        )}

        {/* Error notices */}
        {errors.length > 0 && (
          <div className='tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:mb-4 tw:text-left'>
            <h6 className='tw:font-semibold tw:text-red-800 tw:text-sm tw:mb-2 tw:flex tw:items-center tw:gap-2'>
              <XCircle className='tw:size-4' />
              Calculation Error
            </h6>
            <ul className='tw:text-sm tw:text-red-700 tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
              {errors.map((n) => <li key={n.code}>{n.message}</li>)}
            </ul>
          </div>
        )}

        {/* Warning notices */}
        {warnings.length > 0 && (
          <div className='tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-4 tw:mb-4 tw:text-left'>
            <h6 className='tw:font-semibold tw:text-amber-800 tw:text-sm tw:mb-2 tw:flex tw:items-center tw:gap-2'>
              <AlertTriangle className='tw:size-4' />
              {warnings.length === 1 ? 'Warning' : 'Warnings'}
            </h6>
            <ul className='tw:text-sm tw:text-amber-700 tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
              {warnings.map((n) => <li key={n.code}>{n.message}</li>)}
            </ul>
          </div>
        )}

        {/* Info notices */}
        {isSuccess && infos.length > 0 && (
          <div className='tw:rounded-lg tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-4 tw:mb-4 tw:text-left'>
            <ul className='tw:text-sm tw:text-blue-700 tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
              {infos.map((n) => <li key={n.code}>{n.message}</li>)}
            </ul>
          </div>
        )}

        {/* Calculation details */}
        {isSuccess && calculation && (
          <div className='tw:rounded-lg tw:bg-muted/50 tw:p-4 tw:mb-6 tw:text-left tw:text-sm'>
            <table className='tw:w-full'>
              <tbody className='tw:divide-y'>
                <tr>
                  <td className='tw:py-1.5 tw:text-muted-foreground'>Emission Factor</td>
                  <td className='tw:py-1.5 tw:text-right'>
                    {calculation.emissionFactorValue} {calculation.emissionFactorUnit}
                  </td>
                </tr>
                {calculation.emissionFactorSource && (
                  <tr>
                    <td className='tw:py-1.5 tw:text-muted-foreground'>Factor Source</td>
                    <td className='tw:py-1.5 tw:text-right tw:capitalize'>{calculation.emissionFactorSource}</td>
                  </tr>
                )}
                {calculation.regionFallback && (
                  <tr>
                    <td className='tw:py-1.5 tw:text-muted-foreground'>Region Fallback</td>
                    <td className='tw:py-1.5 tw:text-right'>Yes — factor region differs from activity region</td>
                  </tr>
                )}
                {calculation.yearFallback && (
                  <tr>
                    <td className='tw:py-1.5 tw:text-muted-foreground'>Year Fallback</td>
                    <td className='tw:py-1.5 tw:text-right'>Yes — factor year differs from activity period</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Attached evidence — confirms the Attach step landed. */}
        {uniqueEvidences.length > 0 && (
          <div className='tw:rounded-lg tw:border tw:border-border tw:p-4 tw:mb-4 tw:text-left'>
            <h6 className='tw:font-semibold tw:text-sm tw:mb-2'>
              Evidence attached ({uniqueEvidences.length})
            </h6>
            <ul className='tw:divide-y tw:divide-border tw:text-sm'>
              {uniqueEvidences.map((ev) => (
                <li key={ev._id} className='tw:flex tw:items-center tw:gap-2 tw:py-2'>
                  <i className={`fa-regular ${getMimeIcon(ev.mimeType)} tw:text-ThemeTextMedium`} aria-hidden />
                  <span className='tw:truncate tw:flex-1 tw:min-w-0'>{ev.originalName}</span>
                  <Badge variant='ghost' className='tw:text-[10px]'>
                    {EVIDENCE_TYPE_LABEL[ev.evidenceType]}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className='tw:flex tw:justify-center tw:gap-3 tw:flex-wrap'>
          {!isEditing && (
            <Button variant='outline' onClick={onAddAnother}>
              Add Another
            </Button>
          )}
          <Button variant='outline' onClick={onViewActivities}>
            <List className='tw:size-4' />
            View Activities
          </Button>
          {onViewActivity && isSuccess && (
            <Button onClick={onViewActivity}>
              <ExternalLink className='tw:size-4' />
              View Activity
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
